import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material';
import { AddPhotoAlternateOutlined, ExpandMore } from '@mui/icons-material';
import { TranslationKeys } from '../../types/translation';
import CarouselImage from '../../types/carouselImage';
import CarouselImageCard from './CarouselImageCard';
import ShvkButton from '../../styled/ShvkButton';
import useStore from '../../store/storeContext';

interface Props {
    title: keyof TranslationKeys;
    id: string;
}

function CarouselImageAccordion(props: Props) {
    const { dialog, image, localization, snackbar } = useStore();

    const [newCarouselImage, setNewCarouselImage] = useState<CarouselImage | null>(null);

    function addCarouselImage(): void {
        setNewCarouselImage((state) => ({
            ...state,
            active: false,
            carouselImage: '',
            contentType: '',
            id: null,
            orderNumber:
                image.allCarouselImages.length > 0
                    ? Math.max(...image.allCarouselImages.map((img) => img.orderNumber)) + 1
                    : 1,
            redirectUrl: '',
        }));
    }

    function getCarouselImages(): CarouselImage[] {
        if (newCarouselImage) return [...image.allCarouselImages, newCarouselImage];
        else return image.allCarouselImages;
    }

    async function remove(id: number): Promise<void> {
        try {
            const confirmation = await dialog.getConfirmation();
            if (!confirmation) return;
            await image.deleteCarouselImage(id);
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        }
    }

    async function save(url: string, id: number | null, file: File | null): Promise<void> {
        try {
            if (file) {
                // Adding a new file
                await image.uploadCarouselImages(file, url);
                setNewCarouselImage(null);
            } else {
                // Updating the url to an old file
                // The update only concerns the redirect URL, so only id and redirectUrl fields need to be valid.
                const urlUpdateRequest: CarouselImage = {
                    id: id,
                    active: true,
                    carouselImage: '',
                    contentType: '',
                    orderNumber: 0,
                    redirectUrl: url,
                };
                await image.saveCarouselImage(urlUpdateRequest);
            }
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        }
    }

    const { translate } = localization;

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={props.id + '-content'}
                id={props.id + '-header'}
            >
                <Typography>{translate(props.title)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {getCarouselImages().map((image) => (
                        <Grid item key={image.id} xs={12} sm={6} lg={4}>
                            <CarouselImageCard image={image} save={save} remove={remove} />
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                            <ShvkButton
                                onClick={addCarouselImage}
                                disabled={Boolean(newCarouselImage)}
                                endIcon={<AddPhotoAlternateOutlined />}
                            >
                                {translate('ADD')}
                            </ShvkButton>
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default observer(CarouselImageAccordion);
